import { FieldValues, Path, useFormContext } from 'react-hook-form';
import { useCallback, useMemo, useState } from 'react';
import { FormSelectId, FormSelectIdProps, FormSelectInput } from '@nexelem/form-components';
import { useCustomTranslation } from '../../../hooks';
import { useWarehouseTags } from '../../../queryHooks/useWarehouseTags';

type FormWarehouseTagsSelectIdProps = Omit<FormSelectIdProps, 'label' | 'options' | 'placeholder'>;

export const FormWarehouseTagsSelectId = ({ name, disableClear, maxWidth = 560 }: FormWarehouseTagsSelectIdProps) => {
  const { tWrapper } = useCustomTranslation('warehouse.outboundDeliveriesPlannedForm');
  const { data, isLoading } = useWarehouseTags();

  const options = data?.map((tag) => {
    return { id: tag.id, label: tag.name };
  });

  return (
    <FormSelectId
      isMultiple
      disableClear={disableClear}
      label={tWrapper('warehouseTags')}
      loading={isLoading}
      maxWidth={maxWidth}
      name={name}
      options={options}
      placeholder={tWrapper('warehouseTagsPlaceholder')}
    />
  );
};

type FormWarehouseTagsSelectProps<Value extends FieldValues> = {
  readonly name: Path<Value>;
  readonly isMultiple?: boolean;
};

export const FormWarehouseTagsSelect = <Value extends FieldValues>({
  ...props
}: FormWarehouseTagsSelectProps<Value>) => {
  const { tWrapper } = useCustomTranslation('warehouse.outboundDeliveriesPlannedForm');
  const [isOpen, setIsOpen] = useState(false);
  const onToggle = useCallback(() => setIsOpen((prevState) => !prevState), []);
  const [text, setText] = useState('');

  const { control } = useFormContext<Value>();
  const { data, isLoading } = useWarehouseTags();

  const options = useMemo(
    () =>
      data?.map(({ name, id }) => {
        return { value: id, key: name };
      }),
    [data],
  );

  return (
    <FormSelectInput
      {...props}
      control={control}
      getOptionKey={({ value }) => value.toString()}
      getOptionLabel={({ key }) => key.toString()}
      inputValue={text}
      isOptionEqualToValue={(option, value) => option.value === value.value}
      label={tWrapper('warehouseTags')}
      loading={isLoading}
      open={isOpen}
      options={options}
      placeholder={tWrapper('warehouseTagsPlaceholder')}
      onClose={onToggle}
      onInputChange={setText}
      onOpen={onToggle}
    />
  );
};
